'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus, Trash2, Edit, Power, TestTube, Eye, Check, X, Copy,
  AlertCircle, Activity, Clock, ChevronDown, Filter, Loader2,
  Webhook as WebhookLucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Switch } from '@/components/ui/Switch';
import { TrainerRightPanelHeader } from '@/components/dashboard/TrainerRightPanelHeader';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  secret?: string;
  isActive: boolean;
  lastTriggered?: string;
  createdAt: string;
  filters?: WebhookFilters | null;
  _count?: { logs: number };
}

interface WebhookLog {
  id: string;
  event: string;
  payload: any;
  statusCode?: number;
  responseBody?: string;
  response?: string;
  success: boolean;
  errorMessage?: string;
  duration?: number;
  createdAt: string;
  status?: string;
}

interface AvailableEvent {
  id: string;
  name: string;
  description: string;
  filterType?: string | null;
  changeTypes?: { id: string; label: string }[];
}

interface FilterResource {
  id: string;
  title: string;
  status?: string;
  blocks?: { id: string; title: string; type: string; order: number }[];
}

type WebhookFilters = Record<string, {
  flowIds?: string[]; blockIds?: string[]; projectIds?: string[]; changeTypes?: string[];
}>;

interface FormData {
  name: string;
  url: string;
  events: string[];
  secret: string;
  filters: WebhookFilters;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function WebhookConfig() {
  const { t } = useTranslation();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [availableEvents, setAvailableEvents] = useState<AvailableEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showTestResultModal, setShowTestResultModal] = useState(false);
  const [filterDetailsModal, setFilterDetailsModal] = useState<{
    event: string; filters: WebhookFilters[string]; eventName: string;
  } | null>(null);
  const [testResult, setTestResult] = useState<{
    success: boolean; statusCode?: number; duration?: number;
    error?: string; testedEvent?: string; payload?: any;
  } | null>(null);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [filterResources, setFilterResources] = useState<{ flows: FilterResource[]; projects: FilterResource[] }>({ flows: [], projects: [] });
  const [expandedFilterEvent, setExpandedFilterEvent] = useState<string | null>(null);
  const [pendingDeleteWebhookId, setPendingDeleteWebhookId] = useState<string | null>(null);
  const [deletingWebhook, setDeletingWebhook] = useState(false);

  const [formData, setFormData] = useState<FormData>({ name: '', url: '', events: [], secret: '', filters: {} });

  useEffect(() => { loadWebhooks(); loadAvailableEvents(); }, []);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 3000); return () => clearTimeout(t); } }, [toast]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => setToast({ message, type });

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const resp = await api.get<any>('/institution/webhooks');
      const raw = resp?.data ?? resp;
      const rows: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : raw?.webhooks ?? [];
      setWebhooks(rows.map((w: any) => ({
        id: w.id, name: w.name || w.url, url: w.url,
        events: Array.isArray(w.events) ? w.events : [],
        secret: w.secret, isActive: w.isActive ?? w.enabled ?? true,
        lastTriggered: w.lastTriggered ?? w.lastTriggeredAt ?? null,
        createdAt: w.createdAt ?? '', filters: w.filters || null,
        _count: w._count || undefined,
      })));
    } catch { setWebhooks([]); }
    finally { setLoading(false); }
  };

  const loadAvailableEvents = async () => {
    try {
      const resp = await api.get<any>('/institution/webhooks/events/available');
      const raw = resp?.data ?? resp;
      setAvailableEvents(raw?.events ?? raw?.data ?? []);
    } catch {
      setAvailableEvents([
        { id: 'student.created', name: 'Apprenant inscrit', description: 'Un apprenant est inscrit à une formation' },
        { id: 'student.completed', name: 'Formation terminée', description: "Un apprenant a terminé une formation" },
        { id: 'block.completed', name: 'Bloc terminé', description: "Un apprenant a terminé un bloc", filterType: 'flow+block' },
        { id: 'flow.published', name: 'Formation publiée', description: 'Une formation est publiée', filterType: 'flow' },
        { id: 'flow.updated', name: 'Formation modifiée', description: 'Une formation est modifiée', filterType: 'flow+changes', changeTypes: [{ id: 'content', label: 'Contenu' }, { id: 'settings', label: 'Paramètres' }, { id: 'status', label: 'Statut' }] },
        { id: 'project.published', name: 'Podcast publié', description: 'Un podcast est publié', filterType: 'project' },
        { id: 'certificate.issued', name: 'Certificat délivré', description: 'Un certificat est délivré à un apprenant' },
        { id: 'score.recorded', name: 'Score enregistré', description: "Un score de quiz/évaluation est enregistré", filterType: 'flow+block' },
      ]);
    }
  };

  const loadFilterResources = async () => {
    try {
      const resp = await api.get<any>('/institution/webhooks/filters/resources');
      const raw = resp?.data ?? resp;
      setFilterResources({ flows: raw?.flows ?? [], projects: raw?.projects ?? [] });
    } catch { /* keep empty */ }
  };

  const openWebhookDrawer = (webhook?: Webhook) => {
    if (webhook) {
      setEditingWebhook(webhook);
      setFormData({ name: webhook.name, url: webhook.url, events: webhook.events, secret: webhook.secret || '', filters: (webhook.filters as WebhookFilters) || {} });
    } else {
      setEditingWebhook(null);
      setFormData({ name: '', url: '', events: [], secret: '', filters: {} });
    }
    setExpandedFilterEvent(null);
    setDrawerOpen(true);
    loadFilterResources();
  };

  const closeWebhookDrawer = () => {
    setDrawerOpen(false);
    setEditingWebhook(null);
  };

  const requestCloseWebhookDrawer = () => {
    if (saving) return;
    closeWebhookDrawer();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingWebhook) {
        await api.put(`/institution/webhooks/${editingWebhook.id}`, formData);
        showToast('Webhook mis à jour');
      } else {
        await api.post('/institution/webhooks', formData);
        showToast('Webhook créé');
      }
      closeWebhookDrawer();
      loadWebhooks();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setPendingDeleteWebhookId(id);
  };

  const confirmDeleteWebhook = async () => {
    if (!pendingDeleteWebhookId) return;
    try {
      setDeletingWebhook(true);
      await api.delete(`/institution/webhooks/${pendingDeleteWebhookId}`);
      showToast('Webhook supprimé');
      setPendingDeleteWebhookId(null);
      loadWebhooks();
    } catch {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setDeletingWebhook(false);
    }
  };

  const handleToggleActive = async (webhook: Webhook) => {
    try {
      await api.put(`/institution/webhooks/${webhook.id}`, { isActive: !webhook.isActive });
      showToast(webhook.isActive ? 'Webhook désactivé' : 'Webhook activé');
      loadWebhooks();
    } catch { showToast('Erreur lors du basculement', 'error'); }
  };

  const handleTest = async (id: string) => {
    try {
      showToast('Test en cours…');
      const resp = await api.post<any>(`/institution/webhooks/${id}/test`);
      const raw = resp?.data ?? resp;
      const result = raw?.result ?? raw;
      setTestResult(result);
      setShowTestResultModal(true);
      if (result?.success) {
        showToast(`Test réussi (${result.statusCode ?? '—'}, ${result.duration ?? '—'}ms)`);
      } else {
        showToast(result?.error || 'Échec du test', 'error');
      }
    } catch { showToast('Erreur lors du test', 'error'); }
  };

  const handleViewLogs = async (webhook: Webhook) => {
    try {
      setSelectedWebhook(webhook);
      const resp = await api.get<any>(`/institution/webhooks/${webhook.id}/logs`);
      const raw = resp?.data ?? resp;
      setLogs(raw?.logs ?? raw?.data ?? []);
      setShowLogsModal(true);
    } catch { showToast('Erreur lors du chargement des logs', 'error'); }
  };

  const copyToClipboard = (text: string, label = 'Copié !') => {
    navigator.clipboard.writeText(text);
    showToast(label);
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  // ─── Loading ──────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-primary-600 animate-spin" /></div>;
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Toast */}
      {toast && (
        <div className={cn('fixed top-4 end-4 px-4 py-3 rounded-lg shadow-lg z-50 text-white text-sm font-medium', toast.type === 'success' ? 'bg-[#19335E]' : 'bg-red-500')}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0C1C38] dark:text-white">Webhooks</h2>
          <p className="text-[#5B6472] dark:text-[#B6CAEC] text-sm mt-1">Recevez des notifications en temps réel pour les événements de votre plateforme</p>
        </div>
        <button type="button" onClick={() => openWebhookDrawer()} className="flex items-center gap-2 px-4 py-2 bg-primary-900 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" /> Nouveau webhook
        </button>
      </div>

      {/* Webhook List */}
      {webhooks.length === 0 ? (
        <div className="bg-white dark:bg-[#122644] rounded-xl p-12 text-center border border-[#E8EDF5] dark:border-[#19335E]">
          <Activity className="w-12 h-12 mx-auto text-[#5B6472]/70 mb-4" />
          <h3 className="text-lg font-semibold text-[#0C1C38] dark:text-white mb-2">Aucun webhook configuré</h3>
          <p className="text-[#5B6472] dark:text-[#B6CAEC] mb-6">Les webhooks vous permettent d'intégrer votre plateforme avec des services externes.</p>
          <button type="button" onClick={() => openWebhookDrawer()} className="px-6 py-3 bg-primary-900 hover:bg-primary-700 text-white rounded-lg transition-colors">
            Créer votre premier webhook
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className="bg-white dark:bg-[#122644] rounded-xl p-6 border border-[#E8EDF5] dark:border-[#19335E]">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-[#0C1C38] dark:text-white truncate">{webhook.name}</h3>
                    <span className={cn('px-2 py-0.5 rounded text-xs font-medium', webhook.isActive ? 'bg-[#DCE7F7] text-[#0C1C38] dark:bg-[#F8FAFC]/30 dark:text-[#5C82C5]' : 'bg-[#F0F4FA] text-[#5B6472] dark:bg-[#19335E] dark:text-[#B6CAEC]')}>
                      {webhook.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mb-3 break-all font-mono">{webhook.url}</p>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {webhook.events.map((event) => {
                      const eventInfo = availableEvents.find((e) => e.id === event);
                      const eventFilter = webhook.filters?.[event];
                      const hasFilter = eventFilter && ((eventFilter.flowIds?.length ?? 0) > 0 || (eventFilter.blockIds?.length ?? 0) > 0 || (eventFilter.projectIds?.length ?? 0) > 0 || (eventFilter.changeTypes?.length ?? 0) > 0);
                      return (
                        <span key={event} className={cn('px-2 py-0.5 rounded text-xs flex items-center gap-1', hasFilter ? 'bg-purple-100 text-purple-700 border border-purple-300 cursor-pointer hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700' : 'bg-[#F0F4FA] text-[#5B6472] dark:bg-[#19335E] dark:text-[#DCE7F7]')} title={hasFilter ? 'Cliquer pour voir les filtres' : eventInfo?.description} onClick={() => {
                          if (hasFilter && eventFilter) { loadFilterResources(); setFilterDetailsModal({ event, filters: eventFilter, eventName: eventInfo?.name || event }); }
                        }}>
                          {eventInfo?.name || event}
                          {hasFilter && <Filter className="w-2.5 h-2.5" />}
                        </span>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#5B6472] dark:text-[#B6CAEC]">
                    <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Créé le {formatDate(webhook.createdAt)}</div>
                    {webhook.lastTriggered && <div className="flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Dernier envoi : {formatDate(webhook.lastTriggered)}</div>}
                    {webhook._count && <div className="flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {webhook._count.logs} log(s)</div>}
                  </div>
                </div>
                <div className="flex items-center gap-1 ms-4 shrink-0">
                  <button type="button" onClick={() => handleTest(webhook.id)} className="p-2 text-[#5B6472]/70 hover:text-blue-600 hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] rounded transition-colors" title="Tester"><TestTube className="w-4 h-4" /></button>
                  <button type="button" onClick={() => handleViewLogs(webhook)} className="p-2 text-[#5B6472]/70 hover:text-purple-600 hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] rounded transition-colors" title="Voir les logs"><Eye className="w-4 h-4" /></button>
                  <button type="button" onClick={() => handleToggleActive(webhook)} className={cn('p-2 rounded transition-colors', webhook.isActive ? 'text-[#19335E] hover:text-[#0C1C38] hover:bg-[#F0F4FA] dark:hover:bg-[#19335E]' : 'text-[#5B6472]/70 hover:text-[#5B6472] hover:bg-[#F0F4FA] dark:hover:bg-[#19335E]')} title={webhook.isActive ? 'Désactiver' : 'Activer'}><Power className="w-4 h-4" /></button>
                  <button type="button" onClick={() => openWebhookDrawer(webhook)} className="p-2 text-[#5B6472]/70 hover:text-amber-600 hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] rounded transition-colors" title="Modifier"><Edit className="w-4 h-4" /></button>
                  <button type="button" onClick={() => handleDelete(webhook.id)} className="p-2 text-[#5B6472]/70 hover:text-red-600 hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] rounded transition-colors" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create / Edit : panneau droit (même motif que modèles de blocs / institution) ── */}
      {drawerOpen
        ? createPortal(
            <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start" role="dialog" aria-modal="true">
              <div
                className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
                onClick={requestCloseWebhookDrawer}
                aria-hidden
              />
              <div className="relative z-10 flex h-full w-full max-w-2xl flex-col border-s border-slate-200 bg-white shadow-2xl dark:border-[#19335E] dark:bg-[#0C1C38] ltr:animate-slide-in-right rtl:animate-slide-in-left">
                <TrainerRightPanelHeader
                  icon={WebhookLucideIcon}
                  title={
                    editingWebhook
                      ? t('institution.webhooks.drawerEditTitle', 'Modifier le webhook')
                      : t('institution.webhooks.drawerCreateTitle', 'Nouveau webhook')
                  }
                  description={t(
                    'institution.webhooks.drawerHint',
                    'Définissez l’URL HTTPS, les événements déclenchés et, si besoin, le secret HMAC.',
                  )}
                  onClose={requestCloseWebhookDrawer}
                  closeDisabled={saving}
                  titleId="institution-webhook-drawer-title"
                  supportFabPanelMaxWidth="xl"
                />
                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                  <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                    <div className="space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7] mb-1.5">Nom</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg text-[#0C1C38] dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30" placeholder="Ex: Synchronisation Moodle" required />
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7] mb-1.5">URL cible</label>
                <input type="url" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg text-[#0C1C38] dark:text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30" placeholder="https://api.example.com/webhook" required />
              </div>

              {/* Events */}
              <div>
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7] mb-1.5">Événements</label>
                <div className="space-y-1 max-h-80 overflow-y-auto bg-[#F8FAFC] dark:bg-[#122644]/50 rounded-lg p-4 border border-[#E8EDF5] dark:border-[#19335E]">
                  {availableEvents.map((event) => {
                    const isChecked = formData.events.includes(event.id);
                    const eventFilter = formData.filters[event.id];
                    const hasFilter = eventFilter && ((eventFilter.flowIds?.length ?? 0) > 0 || (eventFilter.blockIds?.length ?? 0) > 0 || (eventFilter.projectIds?.length ?? 0) > 0 || (eventFilter.changeTypes?.length ?? 0) > 0);
                    const isExpanded = expandedFilterEvent === event.id;

                    return (
                      <div key={event.id}>
                        <div className="flex items-start gap-3 py-1.5">
                          <Switch
                            size="sm"
                            className="mt-1 shrink-0"
                            checked={isChecked}
                            onChange={(checked) => {
                              if (checked) {
                                setFormData({ ...formData, events: [...formData.events, event.id] });
                              } else {
                                const nf = { ...formData.filters }; delete nf[event.id];
                                setFormData({ ...formData, events: formData.events.filter((ev) => ev !== event.id), filters: nf });
                                if (expandedFilterEvent === event.id) setExpandedFilterEvent(null);
                              }
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-[#0C1C38] dark:text-white font-medium text-sm">{event.name}</div>
                            <div className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{event.description}</div>
                          </div>
                          {isChecked && event.filterType && (
                            <button type="button" onClick={() => setExpandedFilterEvent(isExpanded ? null : event.id)} className={cn('p-1 rounded transition-colors', hasFilter ? 'text-purple-600 bg-purple-100 dark:bg-purple-900/30' : 'text-[#5B6472]/70 hover:text-[#5B6472]')} title={hasFilter ? 'Filtres configurés' : 'Configurer les filtres'}>
                              <Filter className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        {/* Filter panel */}
                        {isChecked && isExpanded && event.filterType && (
                          <div className="ms-8 mt-2 mb-3 p-3 bg-white dark:bg-[#122644] rounded-lg border border-[#E8EDF5] dark:border-[#19335E]">
                            <div className="text-xs text-[#5B6472] dark:text-[#B6CAEC] mb-2 flex items-center gap-1">
                              <Filter className="w-3 h-3" /> Filtres — <span className="text-[#5B6472]/70">vide = tous</span>
                            </div>

                            {(event.filterType === 'flow' || event.filterType === 'flow+block' || event.filterType === 'flow+changes') && (
                              <div className="space-y-1">
                                <div className="text-xs font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-1">Formations</div>
                                {filterResources.flows.map((flow) => {
                                  const flowSelected = eventFilter?.flowIds?.includes(flow.id) || false;
                                  return (
                                    <div key={flow.id}>
                                      <div className="flex cursor-pointer items-center gap-2 py-0.5">
                                        <Switch
                                          size="sm"
                                          checked={flowSelected}
                                          onChange={(nextChecked) => {
                                            const cur = formData.filters[event.id] || {};
                                            let nfIds = [...(cur.flowIds || [])];
                                            let nbIds = [...(cur.blockIds || [])];
                                            if (nextChecked) { nfIds.push(flow.id); } else { nfIds = nfIds.filter((i) => i !== flow.id); nbIds = nbIds.filter((i) => !(flow.blocks || []).some((b) => b.id === i)); }
                                            setFormData({ ...formData, filters: { ...formData.filters, [event.id]: { ...cur, flowIds: nfIds, blockIds: nbIds } } });
                                          }}
                                        />
                                        <span className="text-xs text-[#0C1C38] dark:text-white truncate">{flow.title}</span>
                                      </div>
                                      {event.filterType === 'flow+block' && flowSelected && flow.blocks && flow.blocks.length > 0 && (
                                        <div className="ms-5 mt-1 mb-1 ps-2 border-s border-[#E8EDF5] dark:border-[#19335E]">
                                          <div className="text-xs text-[#5B6472] dark:text-[#B6CAEC] mb-0.5">Blocs</div>
                                          {flow.blocks.map((block) => (
                                            <div key={block.id} className="flex cursor-pointer items-center gap-2 py-0.5">
                                              <Switch
                                                size="sm"
                                                checked={eventFilter?.blockIds?.includes(block.id) || false}
                                                onChange={(nextChecked) => {
                                                  const cur = formData.filters[event.id] || {};
                                                  const nb = nextChecked ? [...(cur.blockIds || []), block.id] : (cur.blockIds || []).filter((i) => i !== block.id);
                                                  setFormData({ ...formData, filters: { ...formData.filters, [event.id]: { ...cur, blockIds: nb } } });
                                                }}
                                              />
                                              <span className="text-xs text-[#121826] dark:text-[#DCE7F7] truncate">{block.title}</span>
                                              <span className="text-xs text-[#5B6472]/70">({block.type})</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {event.filterType === 'project' && (
                              <div className="space-y-1">
                                <div className="text-xs font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-1">Podcasts</div>
                                {filterResources.projects.map((project) => (
                                  <div key={project.id} className="flex cursor-pointer items-center gap-2 py-0.5">
                                    <Switch
                                      size="sm"
                                      checked={eventFilter?.projectIds?.includes(project.id) || false}
                                      onChange={(nextChecked) => {
                                        const cur = formData.filters[event.id] || {};
                                        const np = nextChecked ? [...(cur.projectIds || []), project.id] : (cur.projectIds || []).filter((i) => i !== project.id);
                                        setFormData({ ...formData, filters: { ...formData.filters, [event.id]: { ...cur, projectIds: np } } });
                                      }}
                                    />
                                    <span className="text-xs text-[#0C1C38] dark:text-white truncate">{project.title}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {event.filterType === 'flow+changes' && event.changeTypes && (
                              <div className="space-y-1 mt-3">
                                <div className="text-xs font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-1">Type de modification</div>
                                {event.changeTypes.map((ct) => (
                                  <div key={ct.id} className="flex cursor-pointer items-center gap-2 py-0.5">
                                    <Switch
                                      size="sm"
                                      checked={eventFilter?.changeTypes?.includes(ct.id) || false}
                                      onChange={(nextChecked) => {
                                        const cur = formData.filters[event.id] || {};
                                        const nc = nextChecked ? [...(cur.changeTypes || []), ct.id] : (cur.changeTypes || []).filter((i) => i !== ct.id);
                                        setFormData({ ...formData, filters: { ...formData.filters, [event.id]: { ...cur, changeTypes: nc } } });
                                      }}
                                    />
                                    <span className="text-xs text-[#0C1C38] dark:text-white truncate">{ct.label}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Secret */}
              <div>
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7] mb-1.5">Secret (HMAC)</label>
                <div className="flex gap-2">
                  <input type="text" value={formData.secret} onChange={(e) => setFormData({ ...formData, secret: e.target.value })} className="flex-1 px-4 py-2 bg-white dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg text-[#0C1C38] dark:text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30" placeholder="Laissez vide pour en générer un automatiquement" />
                  {formData.secret && (
                    <button type="button" onClick={() => copyToClipboard(formData.secret, 'Secret copié !')} className="px-3 py-2 bg-[#F0F4FA] hover:bg-[#E8EDF5] dark:bg-[#19335E] dark:hover:bg-[#B6CAEC] text-[#121826] dark:text-[#DCE7F7] rounded-lg transition-colors"><Copy className="w-4 h-4" /></button>
                  )}
                </div>
                <p className="text-xs text-[#5B6472]/70 mt-1">Utilisé pour signer les requêtes (en-tête X-Webhook-Signature)</p>
              </div>

                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/90 px-5 py-4 dark:border-[#19335E] dark:bg-[#0C1C38]/80 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                    <button
                      type="button"
                      onClick={requestCloseWebhookDrawer}
                      disabled={saving}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-[#B6CAEC] dark:bg-[#122644] dark:text-slate-200 dark:hover:bg-[#19335E]"
                    >
                      {t('institution.common.cancel', 'Annuler')}
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-[color:var(--inst-player-primary,#0C1C38)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[color:var(--inst-player-primary-hover,#19335E)] disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                      {editingWebhook
                        ? t('institution.webhooks.drawerSubmitUpdate', 'Mettre à jour')
                        : t('institution.webhooks.drawerSubmitCreate', 'Créer le webhook')}
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body,
          )
        : null}

      {/* ── Logs Modal ── */}
      {showLogsModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#0C1C38] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-[#E8EDF5] dark:border-[#19335E] shadow-2xl">
            <div className="p-6 border-b border-[#E8EDF5] dark:border-[#19335E] flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#0C1C38] dark:text-white">Logs — {selectedWebhook.name}</h2>
                <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">{logs.length} envoi(s)</p>
              </div>
              <button type="button" onClick={() => { setShowLogsModal(false); setExpandedLogId(null); }} className="p-2 hover:bg-[#F0F4FA] dark:hover:bg-[#122644] rounded-lg transition-colors"><X className="w-4 h-4 text-[#5B6472]/70" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {logs.length === 0 ? (
                <div className="text-center py-12 text-[#5B6472]/70">Aucun log pour ce webhook</div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => {
                    const isExpanded = expandedLogId === log.id;
                    const isSuccess = log.success ?? (log.status === 'success') ?? (log.statusCode != null && log.statusCode >= 200 && log.statusCode < 300);
                    return (
                      <div key={log.id} className={cn('rounded-lg border transition-all', isSuccess ? 'border-[#B6CAEC] bg-[#F0F4FA]/50 dark:border-[#F0F4FA] dark:bg-[#F8FAFC]/10' : 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-900/10')}>
                        <button type="button" onClick={() => setExpandedLogId(isExpanded ? null : log.id)} className="w-full p-4 flex items-center justify-between text-start hover:bg-white/50 dark:hover:bg-white/5 rounded-lg transition-colors">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {isSuccess ? <Check className="w-4 h-4 text-[#19335E] shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />}
                            <span className={cn('text-xs font-mono px-2 py-0.5 rounded', isSuccess ? 'bg-[#DCE7F7] text-[#0C1C38] dark:bg-[#F8FAFC]/30 dark:text-[#5C82C5]' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300')}>{isSuccess ? 'OK' : 'ERR'}</span>
                            <span className="text-[#0C1C38] dark:text-white font-medium text-sm">{log.event}</span>
                            {log.statusCode && <span className={cn('text-xs font-mono px-1.5 py-0.5 rounded', log.statusCode < 300 ? 'bg-[#DCE7F7]/50 text-[#19335E]' : 'bg-red-100/50 text-red-500')}>{log.statusCode}</span>}
                            {log.duration != null && <span className="text-xs text-[#5B6472]">{log.duration}ms</span>}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xs text-[#5B6472]/70">{formatDate(log.createdAt)}</span>
                            <ChevronDown className={cn('w-4 h-4 text-[#5B6472]/70 transition-transform', isExpanded && 'rotate-180')} />
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-4 pb-4 space-y-3 border-t border-[#E8EDF5] dark:border-[#19335E] pt-3">
                            {log.errorMessage && (
                              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                                <p className="text-xs font-medium text-red-500 mb-1">Erreur</p>
                                <p className="text-sm text-red-600 dark:text-red-400 font-mono">{log.errorMessage}</p>
                              </div>
                            )}
                            {log.payload && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-xs font-medium text-[#5B6472] dark:text-[#B6CAEC]">Données envoyées</p>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); copyToClipboard(JSON.stringify(log.payload, null, 2)); }} className="text-xs text-[#5B6472]/70 hover:text-[#121826] dark:hover:text-white flex items-center gap-1 transition-colors"><Copy className="w-3 h-3" /> Copier</button>
                                </div>
                                <pre className="bg-[#F8FAFC] dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg p-3 text-xs text-[#121826] dark:text-[#DCE7F7] font-mono overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">{JSON.stringify(log.payload, null, 2)}</pre>
                              </div>
                            )}
                            {(log.responseBody || log.response) && (
                              <div>
                                <p className="text-xs font-medium text-[#5B6472] dark:text-[#B6CAEC] mb-1">Réponse</p>
                                <pre className="bg-[#F8FAFC] dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg p-3 text-xs text-[#121826] dark:text-[#DCE7F7] font-mono overflow-x-auto max-h-32 overflow-y-auto whitespace-pre-wrap">{log.responseBody || log.response}</pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[#E8EDF5] dark:border-[#19335E]">
              <button type="button" onClick={() => { setShowLogsModal(false); setExpandedLogId(null); }} className="w-full px-4 py-2.5 bg-[#F0F4FA] hover:bg-[#E8EDF5] dark:bg-[#122644] dark:hover:bg-[#19335E] text-[#0C1C38] dark:text-white text-sm font-medium rounded-lg transition-colors">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Test Result Modal ── */}
      {showTestResultModal && testResult && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#0C1C38] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-[#E8EDF5] dark:border-[#19335E] shadow-2xl">
            <div className={cn('p-6 border-b', testResult.success ? 'border-[#B6CAEC] bg-[#F0F4FA] dark:border-[#F0F4FA] dark:bg-[#F8FAFC]/20' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20')}>
              <div className="flex items-center gap-3">
                {testResult.success ? <Check className="w-6 h-6 text-[#19335E]" /> : <X className="w-6 h-6 text-red-500" />}
                <div>
                  <h2 className="text-xl font-bold text-[#0C1C38] dark:text-white">{testResult.success ? 'Test réussi' : 'Échec du test'}</h2>
                  <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">
                    Événement : <span className="text-[#0C1C38] dark:text-white font-medium">{testResult.testedEvent ?? 'webhook.test'}</span>
                    {testResult.statusCode && <> · Status : <span className="text-[#0C1C38] dark:text-white font-medium">{testResult.statusCode}</span></>}
                    {testResult.duration && <> · Durée : <span className="text-[#0C1C38] dark:text-white font-medium">{testResult.duration}ms</span></>}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {testResult.error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <h3 className="text-sm font-semibold text-red-500 mb-2">Erreur</h3>
                  <p className="text-sm text-red-600 dark:text-red-400">{testResult.error}</p>
                </div>
              )}
              {testResult.payload && (
                <div>
                  <h3 className="text-sm font-semibold text-[#5B6472] dark:text-[#DCE7F7] mb-3">Données envoyées</h3>
                  <pre className="bg-[#F8FAFC] dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg p-4 overflow-x-auto text-sm text-[#121826] dark:text-[#DCE7F7] font-mono">{JSON.stringify(testResult.payload, null, 2)}</pre>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-[#E8EDF5] dark:border-[#19335E] flex gap-3">
              {testResult.payload && (
                <button type="button" onClick={() => copyToClipboard(JSON.stringify(testResult.payload, null, 2), 'Payload copié !')} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#F0F4FA] hover:bg-[#E8EDF5] dark:bg-[#122644] dark:hover:bg-[#19335E] text-[#0C1C38] dark:text-white text-sm font-medium rounded-lg transition-colors"><Copy className="w-4 h-4" /> Copier le payload</button>
              )}
              <button type="button" onClick={() => setShowTestResultModal(false)} className="flex-1 px-4 py-2.5 bg-primary-900 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Filter Details Modal ── */}
      {filterDetailsModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setFilterDetailsModal(null)}>
          <div className="bg-white dark:bg-[#0C1C38] rounded-2xl p-6 max-w-md w-full mx-4 border border-[#E8EDF5] dark:border-[#19335E] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-purple-500" />
                <h3 className="text-lg font-semibold text-[#0C1C38] dark:text-white">Filtres actifs</h3>
              </div>
              <button type="button" onClick={() => setFilterDetailsModal(null)} className="p-1 hover:bg-[#F0F4FA] dark:hover:bg-[#122644] rounded transition-colors"><X className="w-4 h-4 text-[#5B6472]/70" /></button>
            </div>
            <div className="mb-3">
              <span className="px-2 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded text-xs border border-purple-200 dark:border-purple-700">{filterDetailsModal.eventName}</span>
            </div>
            <div className="space-y-4">
              {filterDetailsModal.filters.flowIds && filterDetailsModal.filters.flowIds.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-2">Formations</div>
                  <div className="space-y-1">
                    {filterDetailsModal.filters.flowIds.map((flowId) => {
                      const flow = filterResources.flows.find((f) => f.id === flowId);
                      return <div key={flowId} className="flex items-center gap-2 px-3 py-1.5 bg-[#F8FAFC] dark:bg-[#122644] rounded text-sm text-[#0C1C38] dark:text-white"><Check className="w-3.5 h-3.5 text-[#19335E] shrink-0" /><span className="truncate">{flow?.title || flowId}</span></div>;
                    })}
                  </div>
                </div>
              )}
              {filterDetailsModal.filters.blockIds && filterDetailsModal.filters.blockIds.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-2">Blocs</div>
                  <div className="space-y-1">
                    {filterDetailsModal.filters.blockIds.map((blockId) => {
                      let blockName = blockId, flowName = '';
                      for (const flow of filterResources.flows) { const b = flow.blocks?.find((bl) => bl.id === blockId); if (b) { blockName = b.title; flowName = flow.title; break; } }
                      return <div key={blockId} className="flex items-center gap-2 px-3 py-1.5 bg-[#F8FAFC] dark:bg-[#122644] rounded text-sm text-[#0C1C38] dark:text-white"><Check className="w-3.5 h-3.5 text-[#19335E] shrink-0" /><span className="truncate">{blockName}</span>{flowName && <span className="text-xs text-[#5B6472]/70 shrink-0">({flowName})</span>}</div>;
                    })}
                  </div>
                </div>
              )}
              {filterDetailsModal.filters.projectIds && filterDetailsModal.filters.projectIds.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-2">Podcasts</div>
                  <div className="space-y-1">
                    {filterDetailsModal.filters.projectIds.map((pid) => {
                      const project = filterResources.projects.find((p) => p.id === pid);
                      return <div key={pid} className="flex items-center gap-2 px-3 py-1.5 bg-[#F8FAFC] dark:bg-[#122644] rounded text-sm text-[#0C1C38] dark:text-white"><Check className="w-3.5 h-3.5 text-[#19335E] shrink-0" /><span className="truncate">{project?.title || pid}</span></div>;
                    })}
                  </div>
                </div>
              )}
              {filterDetailsModal.filters.changeTypes && filterDetailsModal.filters.changeTypes.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-2">Types de modification</div>
                  <div className="space-y-1">
                    {filterDetailsModal.filters.changeTypes.map((ct) => {
                      const evInfo = availableEvents.find((e) => e.id === filterDetailsModal.event);
                      const ctInfo = evInfo?.changeTypes?.find((c) => c.id === ct);
                      return <div key={ct} className="flex items-center gap-2 px-3 py-1.5 bg-[#F8FAFC] dark:bg-[#122644] rounded text-sm text-[#0C1C38] dark:text-white"><Check className="w-3.5 h-3.5 text-[#19335E] shrink-0" /><span className="truncate">{ctInfo?.label || ct}</span></div>;
                    })}
                  </div>
                </div>
              )}
            </div>
            <button type="button" onClick={() => setFilterDetailsModal(null)} className="w-full mt-5 px-4 py-2.5 bg-primary-900 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors">Fermer</button>
          </div>
        </div>
      )}
      <Modal
        open={Boolean(pendingDeleteWebhookId)}
        onClose={() => {
          if (!deletingWebhook) setPendingDeleteWebhookId(null);
        }}
        size="sm"
        closeOnBackdrop={!deletingWebhook}
      >
        <ModalHeader>
          <h3 className="text-base font-semibold text-[#0C1C38] dark:text-white">Confirmer la suppression</h3>
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-[#5B6472] dark:text-[#DCE7F7]">
            Supprimer ce webhook définitivement ?
          </p>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            onClick={() => setPendingDeleteWebhookId(null)}
            disabled={deletingWebhook}
            className="flex-1 px-4 py-2.5 bg-[#F0F4FA] hover:bg-[#E8EDF5] dark:bg-[#122644] dark:hover:bg-[#19335E] text-[#0C1C38] dark:text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void confirmDeleteWebhook()}
            disabled={deletingWebhook}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            {deletingWebhook ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Supprimer
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
